import React, {useEffect, useState} from 'react';
import axios, {AxiosResponse} from "axios";
import ProfileDataRow from "./ProfileDataRow";
import CodePanel from "./CodePanel";
import {Panel} from "primereact/panel";
import EditableField from "./EditableField";
import {useAuth} from "../useAuth";

const OrganizationAccount = () => {

    const {ogToken, ogRefreshToken, ogOrgId, ogAccessPoint, setOgAccessPoint} = useAuth();

    const [ogOrgName, setOgOrgName] = useState("");
    const [ogOrgDesc, setOgOrgDesc] = useState("");
    const [ogCountryCode, setOgCountryCode] = useState(null);
    const [ogMonthlyQuota, setOgMonthlyQuota] = useState(null);

    const objectsGridOrgUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/organizationaccounts/';

    useEffect(() => {
        if (!ogToken) {
            console.log("No ObjectsGrid access token in useEffect.");
            return;
        }

        if (!ogOrgId) {
            console.log("No OrgId in useEffect.");
            return;
        }

        if (!ogAccessPoint) {
            console.log("No ogAccessPoint in useEffect.");
            return;
        }

        getOrganisation(ogOrgId, ogToken, ogAccessPoint).then(() => {
            console.log('Organisation fetched successfully');
        }).catch(() => {
            const accessPointToTry = ogAccessPoint === 'EU' ? 'US' : 'EU';
            console.log("Failed to fetch org. Perhaps this is not configured for the " + ogAccessPoint + " access point. Trying " + accessPointToTry);
            getOrganisation(ogOrgId, ogToken, accessPointToTry).then(() => {
                console.log('Organisation fetched successfully');
                setOgAccessPoint(accessPointToTry);
            }).catch(() => {
                console.error("Failed to fetch org for both AccessPoints. There must be another problem.");
            });
        });

    }, []);

    const getOrganisation = (ogOrgId: any, ogToken: any, ogAccessPoint: any) => {
        return new Promise((resolve, reject) => {
            console.log('About to get organisation:', ogOrgId, ogToken);

            if (!ogOrgId) return reject('Organization ID is required');
            if (!ogToken) return reject('ObjectsGrid token is required');
            if (!ogAccessPoint) return reject('ObjectsGrid ogAccessPoint is required');

            ogOrgId = ogOrgId.toLowerCase();

            axios.get(objectsGridOrgUrl + ogOrgId, {
                headers: {
                    'Content-Type': 'application/json',
                     'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            })
                .then(response => {
                    console.log('Response from getOrg:', response.data);
                    setOrgValues(response);
                    resolve(response.data);
                })
                .catch(error => {
                    console.error('Getting organisation failed:', error);
                    reject(error);
                });
        });
    };

    const getOrganisationOld = async (ogOrgId: string, ogToken: string, ogAccessPoint: string) => {

        console.log('About to get organisation:', ogOrgId, ogToken);

        if (!ogOrgId) return Promise.reject('Organization ID is required');
        if (!ogToken) return Promise.reject('ObjectsGrid token is required');
        if (!ogAccessPoint) return Promise.reject('ObjectsGrid ogAccessPoint is required');

        return axios.get(objectsGridOrgUrl + ogOrgId,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            }).then(response => {
            console.log('Response from getOrg:', response.data);
            setOrgValues(response);

        }).catch(error => console.error('Getting organisation failed:', error));
    }

    const setOrgValues = (response: AxiosResponse<any, any>) => {
        setOgOrgName(response.data.name);
        setOgOrgDesc(response.data.description);
        setOgCountryCode(response.data.countryCode);
        setOgMonthlyQuota(response.data.monthlyQuota);
    }

    const getDataObject = (ogOrgId: string | null) => {
        return {
            id: ogOrgId,
            name: ogOrgName,
            description: ogOrgDesc,
            countryCode: ogCountryCode,
            accessPoint: ogAccessPoint,
            monthlyQuota: ogMonthlyQuota,
        };
    }

    const updateOrgName = async (newName: string) => {
        console.log('Updating organisation name:', newName);
        const data = getDataObject(ogOrgId);
        data.name = newName;
        await updateOrg(data);
    }

    const updateOrgDesc = async (newDesc: string) => {
        console.log('Updating organisation desc:', newDesc);
        const data = getDataObject(ogOrgId);
        data.description = newDesc;
        await updateOrg(data);
    }

    const updateOrg = async (data: {
        id: string | null;
        name: string;
        description: string;
        countryCode: null;
        accessPoint: string | null;
        monthlyQuota: null;
    }) => {

        if (!ogOrgId) return Promise.reject('Organization ID is required');
        if (!ogToken) return Promise.reject('ObjectsGrid token is required');
        if (!ogAccessPoint) return Promise.reject('ObjectsGrid ogAccessPoint is required');

        return axios.put(objectsGridOrgUrl + ogOrgId,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            }).then(response => {
            console.log('Response from updateOrg:', response.data);
            setOrgValues(response);
        }).catch(error => console.error('Updating organisation failed:', error));
    }

    return (
        !ogOrgId ? (
            <div>Not logged in</div>
        ) : (
            <div className="grid">
                <div className="col-12">
                    <Panel header="Organization Details">
                        <ProfileDataRow label="Organisation ID" value={ogOrgId}/>
                        <EditableField label="Organisation Name" value={ogOrgName}
                                       onChange={(newVal) => updateOrgName(newVal)}/>
                        <EditableField label="Organisation Description" value={ogOrgDesc}
                                       onChange={(newVal) => updateOrgDesc(newVal)}/>
                        <ProfileDataRow label="Country Code" value={ogCountryCode}/>
                        <ProfileDataRow label="Access Point" value={ogAccessPoint}/>
                        <ProfileDataRow label="Monthly Quota" value={ogMonthlyQuota}/>
                    </Panel>
                </div>
                <div className="col-12">
                    <div className="grid">
                        <div className="col-12">
                            <CodePanel title="ObjectsGrid Access Token" code={ogToken}/>
                        </div>
                        <div className="col-12">
                            <CodePanel title="ObjectsGrid Refresh Token" code={ogRefreshToken}/>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

export default OrganizationAccount;